import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../store';
import { Company, companyActions } from '../store/company';
import { FlowStage, flowActions } from '../store/flow';

import style from '../assets/CompanyInformation.module.css';
const {
  USER_INFORMATION_URL, THANK_YOU_URL, CPG_LEGAL_NAME_REGEX,
  CPG_NAME_REGEX, POSTAL_CODE_REGEX, AOF_URL, STATE_API_URL,
  USER_UPDATE_API_URL, PO_BOX_REGEX, CITY_REGEX, USER_UPDATE_API_URL_PASSTHROUGH, ERROR_URL, ADDRESS_VERIFY_URL
} = require('../helper/application-conf').default;
import { Person, User } from '../store/user';
import useAxiosBuyer from '../hooks/useAxiosBuyer';
import { createPayload, userProfilePayload } from '../helper/payloadHelper';
import MessageBanner from './MessageBanner';
import useAxiosSecurity from '../hooks/useAxiosSecurity';
import { useAuth } from '../hooks/useAuth';
import { axiosBuyerPassthrough, axiosSecurityPassthrough } from '../hooks/axios';
import { StorageService } from '../helper/storage-service';
import { Session } from 'inspector';
import { OneIdentityService } from '../service/oneIdentityService';

const countryKeys = ['USA'];
const {
  dlsBrightBlue, Label, Input, PhoneInput, Select, SelectOption, ButtonPrimary, ButtonSecondary, Alert, Tooltip, TooltipButton, GlyphInfo, DialogBox, DialogBoxHeader, DialogBoxBody,
} = require('@americanexpress/dls-react');

const CompanyInformation: React.FC<any> = (props: any) => {
  const [submissionInProgress, setSubmissionInProgress] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const user: User = useAppSelector((state) => state.user);
  const authorizedOfficer: Person = useAppSelector((state) => state.authorizedOfficer);
  const company: Company = useAppSelector((state) => state.company);
  const axiosBuyer = useAxiosBuyer();
  const axiosSecurity = useAxiosSecurity();
  const appState = useAppSelector((state) => state);
  const [count, setCount] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [provinces, setProvinces] = useState<any>([{}]);
  const { auth } = useAuth() as any;
  const axiosHandlerBuyer = props.oneIdentityEnabled ? axiosBuyerPassthrough : axiosBuyer;
  const axiosHandlerSecurity = props.oneIdentityEnabled ? axiosSecurityPassthrough : axiosSecurity;
  const storage = new StorageService();
  const oneIdentityService = new OneIdentityService();
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showAddressNotFound, setShowAddressNotFound] = useState(false);
  const [recommendedAddress, setRecommendedAddress] = useState({
    street1: '',
    street2: '',
    zip: '',
    city: '',
    country: '',
    state: '',
  });


  const showError = (flag: boolean) => {
    setCount((c) => c + 1);
    setHasError(flag);
    setSubmissionInProgress(false);
  };

  const { t } = useTranslation();
  const appDispatch = useAppDispatch();
  const navigate = useNavigate();
  const SITE_NAME = window.location.pathname.split('/')[2];
  const updateField = useCallback((field: string, value: string, e: any = undefined) => {
    if (e && e.target.getAttribute('numeric') === 'true' && e.target.value.match(/\D/)) {
      return;
    }
    appDispatch(companyActions.setCompany({
      ...company,
      [field]: value,
    }));
  }, [company, appDispatch]);

  useEffect(() => {
    const controller = new AbortController();
    const getStateInfo = async () => {
      await axiosHandlerBuyer.get(`${STATE_API_URL}`, {
        signal: controller.signal,
      }).then((stateInfo) => {
        setProvinces(stateInfo.data);
      }, (error) => {
        console.log(error);
      });
    };
    getStateInfo();
    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    appDispatch(flowActions.setStage(FlowStage.CompanyInformation));
    updateField('country', countryKeys[0]);
  }, [appDispatch, updateField]);

  const checkSameDigits = (N: string) => {
    for (let i = 0; i < N.length; i += 1) {
      if (N[0] !== N[i]) {
        return false;
      }
    }
    return true;
  };

  const validateTaxid = useCallback((invalid: any) => {
    if (company.taxId.length === 0) {
      invalid.push('taxId');
    }
    if ((company.taxId.length > 0 && checkSameDigits(company.taxId)) || (company.taxId.length > 0 && company.taxId.length < 9) || company.taxId.toString() === '123456789' || company.taxId.toString() === '987654321') {
      invalid.push('taxid-invalid');
    }
    return invalid;
  }, [company]);
  const invalidFields = useCallback((submitting = false) => {
    if (!hasSubmitted && !submitting) {
      return [];
    }
    const invalid = [];
    if (company.entityName.length === 0) {
      invalid.push('entityName');
    }
    if (company.entityName.length > 40) {
      invalid.push('entityName-length');
    }
    if (company.entityName !== '' && !company.entityName.match(CPG_LEGAL_NAME_REGEX)) {
      invalid.push('entityName-regex');
    }
    if (company.dba.length > 40) {
      invalid.push('dba-length');
    }
    if (company.dba !== '' && !company.dba.match(CPG_NAME_REGEX)) {
      invalid.push('dba-regex');
    }
    if(company.extension.length > 10) {
      invalid.push('extension-length');
    }
    if (company.corporatePhone.length < 10) {
      invalid.push('corporatePhone');
    }
    if (company.street1.length === 0) {
      invalid.push('street1');
    }
    if (company.street1.length > 40) {
      invalid.push('street1-length');
    }
    if (company.street1 !== '' && !company.street1.match(CPG_NAME_REGEX)) {
      invalid.push('street1-regex');
    }
    if (company.street1 !== '' && company.street1.match(PO_BOX_REGEX)) {
      invalid.push('street1-poBox');
    }
    if (company.street2.length > 40) {
      invalid.push('street2-length');
    }
    if (company.street2 !== '' && !company.street2.match(CPG_NAME_REGEX)) {
      invalid.push('street2-regex');
    }
    if (company.street2 !== '' && company.street2.match(PO_BOX_REGEX)) {
      invalid.push('street2-poBox');
    }
    if (company.city.length === 0) {
      invalid.push('city');
    }
    if (company.city.length > 30) {
      invalid.push('city-length');
    }
    if (company.city !== '' && !company.city.match(CITY_REGEX)) {
      invalid.push('city-regex');
    }
    if (company.state.length === 0) {
      invalid.push('state');
    }
    if (company.zip.length === 0) {
      invalid.push('zip');
    }
    if (company.zip.length > 0 && !(POSTAL_CODE_REGEX.test(company.zip))) {
      invalid.push('zipcode-regex');
    }
    if (company.country.length === 0) {
      invalid.push('country');
    }
    const invalidTaxId = validateTaxid(invalid);
    invalid.join(invalidTaxId);
    return invalid;
  }, [company, hasSubmitted, validateTaxid]);

  const fieldStatusToDisplay = useCallback((field: any) => {
    if (invalidFields().some((item) => { return field.includes(item); })) {
      return 'error';
    }
    return 'default';
  }, [invalidFields]);

  const fieldStatus = useCallback((field: string) => {
    if (invalidFields().includes(field)) {
      return 'error';
    }
    return 'default';
  }, [invalidFields]);

  const submitUserCompanyInfo = useCallback(async () => {
    try {
      const controller = new AbortController();
      await axiosHandlerBuyer.get(`${AOF_URL}${SITE_NAME}`, {
        signal: controller.signal,
      }).then(async (getResponse) => {
        if (getResponse.status !== 200 || getResponse === null) {
          showError(true);
        }
        setTimeout(async () => {
          const role = JSON.parse(storage.getSession('role') as any) || null;
          const userPayload: any = userProfilePayload(store, props.oneIdentityEnabled ? role?.originalRole?.id : auth?.role?.originalRole?.id, props.oneIdentityEnabled ? role?.originalRole?.name : auth?.role?.originalRole?.name);
          const userId = props.oneIdentityEnabled ? role.userId : auth.role.userId;
          const url = props.oneIdentityEnabled ? `${USER_UPDATE_API_URL_PASSTHROUGH}` : `${USER_UPDATE_API_URL}${userId}`;
          if (props.oneIdentityEnabled) {
            userPayload['userId'] = role.userId
          }
          await axiosHandlerSecurity.put(url, userPayload).then((putResponse) => {
            if (putResponse.status !== 200 || putResponse === null) {
              showError(true);
            } else {
              const payload: any = { ...appState };
              delete payload.flow;
              if (appState.user.authorizedOfficer) {
                delete payload.authorizedOfficer;
              }
              appDispatch(flowActions.setStage(FlowStage.ThankYou));
              navigate(THANK_YOU_URL);
            }
          }, () => {
            showError(true);
          });
        }, 1);
        const store = [user, authorizedOfficer, company];
        const payload = createPayload(store, getResponse);
        payload.formSubmitted = true;
        await axiosHandlerBuyer.put(`${AOF_URL}${SITE_NAME}`, payload).then((putResponse) => {
          if (putResponse.status !== 200 || putResponse === null) {
            showError(true);
          } else {
            const payload: any = { ...appState };
            delete payload.flow;
            if (appState.user.authorizedOfficer) {
              delete payload.authorizedOfficer;
            }
            appDispatch(flowActions.setStage(FlowStage.ThankYou));
            navigate(THANK_YOU_URL);
          }
        }, () => {
          showError(true);
        });
      }, () => {
        showError(true);
      });
    } catch (err) {
      showError(true);
    }
  }, [SITE_NAME, appDispatch, appState, authorizedOfficer, axiosHandlerBuyer, company, navigate, user]);

  const next = useCallback(async () => {
    setHasSubmitted(true);
    if (invalidFields(true).length === 0) {
      const payload = {
        street_line_1: company.street1,
        street_line_2: company.street2,
        city: company.city,
        state: company.state,
        postal_code: company.zip,
        country: company.country
      };
      const recommended = await verifyAddress(payload);
      if (recommended) {
        const isSameAddress = areAddressesEqual(company, recommended);
        if (isSameAddress) {
          submitUserCompanyInfo();
          setSubmissionInProgress(true);
        }
        else {
          setShowAddressModal(true);
        }
      }
    }
  }, [invalidFields, submitUserCompanyInfo]);

  const verifyAddress = async (payload: {
    street_line_1: string;
    street_line_2: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
  }) => {
    try {
      const response = await axiosHandlerBuyer.put(`${ADDRESS_VERIFY_URL}`, payload);
      const address = response.data.data[0];
      if (address.addressRecommended) {
        const recommended = {
          street1: address.addressLine1 || '',
          street2: address.addressLine2 || '',
          city: address.city || '',
          zip: address.postalCode || '',
          country: address.country || '',
          state: address.state || '',
        };
        setRecommendedAddress(recommended);
        return recommended;
      }
      else {
        setShowAddressNotFound(true);
        return null;
      }
    } catch (error) {
      return false;
    }
  };

  const handleUseRecommended = () => {
    const updatedCompany = {
      ...company,
      ...recommendedAddress
    };
    appDispatch(companyActions.setCompany(updatedCompany));
    setShowAddressModal(false);
  };

  const areAddressesEqual = (addr1: any, addr2: any): boolean => {
    if (!addr1 || !addr2) return false;
    const keysToCompare = ['street1', 'street2', 'city', 'state', 'zip'];
    return keysToCompare.every(
      key => ((addr1[key] || '').trim().toLowerCase()) === ((addr2[key] || '').trim().toLowerCase())
    );
  };

  const back = useCallback(() => {
    appDispatch(flowActions.setStage(FlowStage.UserInformation));
    navigate(USER_INFORMATION_URL);
  }, [appDispatch, navigate]);


  return (
    <div className="container">
      <span key={count}>
        <MessageBanner type="warning" messageText={t('genericError')} showMessage={hasError} classes="col-md-offset-2 col-md-8" />
      </span>
      <div className="row pad-t">
        <h5 className="col-md-offset-2 col-md-8 heading-5">{t('company.company_info')}</h5>
      </div>

      <div className="row pad-t">
        <h4 className="col-md-offset-2 col-md-8 heading-4">{t('company.company_details')}</h4>
      </div>
      <form>
        <div className="row pad-t">
          <div className="col-md-offset-2 col-md-4">
            <Label id="entity-name-label" htmlFor="entity-name-input">
              <span>{t('company.entity_name')}</span>
            </Label>
            <Input id="entity-name-input" maxLength={40} value={company.entityName} required={true} status={fieldStatusToDisplay(['entityName', 'entityName-regex'])} onChange={(e: any) => updateField('entityName', e.target.value)} />
            {fieldStatus('entityName') === 'error'
              && <Alert className="pad-1-t">{t('errors.legalname_required_field')}</Alert>}
            {fieldStatus('entityName-regex') === 'error'
              && <Alert className="pad-1-t">{t('errors.legalname_invalid')}</Alert>}
              {fieldStatus('entityName-length') === 'error'
              && <Alert className="pad-1-t">{t('errors.forty_characters_length_error')}</Alert>}
          </div>
          <div className="col-md-4">
            <Label className={style.tooltipAlignmentHack} id="tax-id-label" htmlFor="tax-id-input">
              {t('company.tax_id')}
            </Label>
            <Input
              id="tax-id-input"
              maxLength={9}
              numeric="true"
              value={company.taxId}
              required={true}
              status={fieldStatusToDisplay(['taxId', 'taxid-invalid'])}
              onChange={(e: any) => updateField('taxId', e.target.value, e)}
            />
            {fieldStatus('taxId') === 'error'
              && <Alert className="pad-1-t">{t('errors.taxnum_required_field')}</Alert>}
            {fieldStatus('taxid-invalid') === 'error'
              && <Alert className="pad-1-t">{t('errors.taxid_invalid')}</Alert>}
          </div>
        </div>
        <div className="row pad-t">
          <div className="col-md-offset-2 col-md-4">
            <Label id="dba-label" htmlFor="dba-input">
              {t('company.dba')}
            </Label>
            <Input id="dba-input" maxLength={40} value={company.dba} onChange={(e: any) => updateField('dba', e.target.value)} />
            {fieldStatus('dba-regex') === 'error'
              && <Alert className="pad-1-t">{t('errors.dbaname_invalid')}</Alert>}
              {fieldStatus('dba-length') === 'error'
              && <Alert className="pad-1-t">{t('errors.forty_characters_length_error')}</Alert>}
          </div>
        </div>
        <div className="row pad-t">
          <h4 className="col-md-offset-2 col-md-8 heading-4">{t('company.company_contact_details')}</h4>
        </div>
        <div className="row pad-t">
          <div className="col-md-offset-2 col-md-4">
            <Label id="corporate-phone-label" htmlFor="corporate-phone-input">
              {t('company.corporate_phone')}
            </Label>
            <PhoneInput id="corporate-phone-input" value={company.corporatePhone} status={fieldStatusToDisplay(['corporatePhone'])} onChange={(e: any) => updateField('corporatePhone', e.target.value)} showFlag={true} showCallingCode={true} callingCode="1" phoneFormat="(###) ###-####" />
            {fieldStatus('corporatePhone') === 'error'
              && <Alert className="pad-1-t">{t('errors.invalid_phone')}</Alert>}
          </div>
          <div className="col-md-4">
            <Label id="extension-label" htmlFor="extension-input">
              {t('company.extension')}
            </Label>
            <Input id="extension-input" maxLength={10} numeric="true" value={company.extension} onChange={(e: any) => updateField('extension', e.target.value, e)} required={false} />
            {fieldStatus('extension-length') === 'error'
              && <Alert className="pad-1-t">{t('errors.ten_characters_length_error')}</Alert>}
          </div>
        </div>
        <div className="row pad-t">
          <div className="col-md-offset-2 col-md-4">
            <Label id="street-1-label" htmlFor="street-1-input">
              <span>{t('company.street_1')}</span>
              <Tooltip aria-labelledby="street-1-label" info={true} placement="right" triggerElement={<TooltipButton info={true} icon={<GlyphInfo theme={dlsBrightBlue} size="lg" />} />}>
                <p className="body-1 pad-1-t">{t('company.street_1_tooltip')}</p>
              </Tooltip>
            </Label>
            <Input id="street-1-input" maxLength={40} value={company.street1} required={true} status={fieldStatusToDisplay(['street1'])} onChange={(e: any) => updateField('street1', e.target.value)} />
            {fieldStatus('street1') === 'error'
              && <Alert className="pad-1-t">{t('errors.address1_required_field')}</Alert>}
            {fieldStatus('street1-regex') === 'error'
              && <Alert className="pad-1-t">{t('errors.street1_invalid')}</Alert>}
            {fieldStatus('street1-poBox') === 'error'
              && <Alert className="pad-1-t">{t('errors.addressHasPoBox')}</Alert>}
            {fieldStatus('street1-length') === 'error'
              && <Alert className="pad-1-t">{t('errors.forty_characters_length_error')}</Alert>}
          </div>
          <div className="col-md-4">
            <Label className={style.tooltipAlignmentHack} id="street-2-label" htmlFor="street-2-input">
              {t('company.street_2')}
            </Label>
            <Input id="street-2-input" maxLength={40} value={company.street2} required={false} onChange={(e: any) => updateField('street2', e.target.value)} />
            {fieldStatus('street2-regex') === 'error'
              && <Alert className="pad-1-t">{t('errors.street2_invalid')}</Alert>}
            {fieldStatus('street2-poBox') === 'error'
              && <Alert className="pad-1-t">{t('errors.addressHasPoBox')}</Alert>}
            {fieldStatus('street2-length') === 'error'
              && <Alert className="pad-1-t">{t('errors.forty_characters_length_error')}</Alert>}
          </div>
        </div>
        <div className="row pad-t">
          <div className="col-md-offset-2 col-md-4">
            <Label id="city-label" htmlFor="city-input">
              {t('company.city')}
            </Label>
            <Input id="city-input" maxLength={30} value={company.city} required={true} status={fieldStatusToDisplay(['city'])} onChange={(e: any) => updateField('city', e.target.value)} />
            {fieldStatus('city') === 'error'
              && <Alert className="pad-1-t">{t('errors.city_required_field')}</Alert>}
            {fieldStatus('city-regex') === 'error'
              && <Alert className="pad-1-t">{t('errors.city_invalid')}</Alert>}
            {fieldStatus('city-length') === 'error'
              && <Alert className="pad-1-t">{t('errors.thirty_characters_length_error')}</Alert>}
          </div>
          <div className="col-md-4">
            <Label id="state-label" htmlFor="state-input">
              {t('company.state')}
            </Label>
            <Select id="state-input" value={company.state} required={true} status={fieldStatusToDisplay(['state'])} onChange={(e: any) => updateField('state', e.target.value)} fluid={true}>
              <SelectOption value="" />
              {provinces.map((key: any) => (
                <SelectOption key={key.name} value={key.stateIsoCode}>
                  {key.name}-{key.stateIsoCode}
                </SelectOption>
              ))}
            </Select>
            {fieldStatus('state') === 'error'
              && <Alert className="pad-1-t">{t('errors.state_required_field')}</Alert>}
          </div>
        </div>
        <div className="row pad-t">
          <div className="col-md-offset-2 col-md-4">
            <Label id="zip-label" htmlFor="zip-input">
              {t('company.zip')}
            </Label>
            <Input id="zip-input" maxLength={10} numeric="true" value={company.zip} required={true} status={fieldStatusToDisplay(['zip', 'zipcode-regex'])} onChange={(e: any) => updateField('zip', e.target.value)} />
            {fieldStatus('zip') === 'error'
              && <Alert className="pad-1-t">{t('errors.postalcode_required_field')}</Alert>}
            {fieldStatus('zipcode-regex') === 'error'
              && <Alert className="pad-1-t">{t('errors.zip_invalid')}</Alert>}
          </div>
          <div className="col-md-4">
            <Label id="country-label" htmlFor="country-input">
              {t('company.country')}
            </Label>
            <Select id="country-input" value={countryKeys[0]} required={true} status={fieldStatusToDisplay(['country'])} onChange={(e: any) => updateField('country', e.target.value)} fluid={true} disabled="true">
              <SelectOption value="" />
              {countryKeys.map((key) => (
                <SelectOption key={key} value={key}>{t(`countries.${key}`)}</SelectOption>
              ))}
            </Select>
            {fieldStatus('country') === 'error'
              && <Alert className="pad-1-t">{t('errors.country_required_field')}</Alert>}
          </div>
        </div>
        <div className={`pad-4-t pad-4-b ${style.flex}`}>
          <h4 className={`heading-4 ${style.backButton}`}>
            <ButtonSecondary size="md" onClick={back}>{t('back')}</ButtonSecondary>
          </h4>
          <h4 className={`heading-4 ${style.submitButton}`}>
            <ButtonPrimary size="md" onClick={next} disabled={submissionInProgress}>{t('submit')}</ButtonPrimary>
          </h4>
        </div>
      </form>

      {showAddressModal && (
        <DialogBox id="show-address-modal">
          <DialogBoxHeader id="show-address-modal-header">
            <p>{t('confirmAddress.title')}</p>
          </DialogBoxHeader>
          <DialogBoxBody id="show-address-modal-body">
            <p className={`pad-2-t margin-2-b text-size-large text-font-size-1 ${style.addressModalSubtitle}`}>
              {t('confirmAddress.subtitle')}
            </p>
            <div className={`pad-4-t pad-4-b flex flex-justify-between`}>
              {/* Original Address Card */}
              <div className={`flex-1 pad-2-t pad-2-b flex-align-items-center dls-gray-01-bg ${style.addressCard}`}>
                <h4 className="col-md-offset-2 col-md-8 heading-2 pad-1-b"> {t('confirmAddress.originalAddress.title')}</h4>
                <p className={`pad-1-b`}>{company.street1 + '   ' + company.street2}</p>
                <p className={`pad-2-b`}>{company.city + '  ' + company.state + '  ' + company.zip}</p>
                <ButtonPrimary id="show-address-modal-button" size="md"
                  onClick={() => {
                    setShowAddressModal(false);
                  }}
                >
                  {t('confirmAddress.originalAddress.button')}
                </ButtonPrimary>
              </div>
              {/* Recommended Address Card */}
              <div className={`flex-1 pad-2-t pad-2-b flex-align-items-center dls-gray-01-bg ${style.addressCard}`}>
                <h4 className="col-md-offset-2 col-md-8 heading-2 pad-1-b"> {t('confirmAddress.recommendedAddress.title')}</h4>
                <p className={`pad-1-b`}>{recommendedAddress.street1 + '' + recommendedAddress.street2}</p>
                <p className={`pad-2-b`}>{recommendedAddress.city + ' ' + recommendedAddress.state + ' ' + recommendedAddress.zip}</p>
                <ButtonPrimary id="recommended-address-button" size="md"
                  onClick={handleUseRecommended}
                >
                  {t('confirmAddress.recommendedAddress.title')}
                </ButtonPrimary>
              </div>
            </div>
          </DialogBoxBody>
        </DialogBox>
      )}
      {showAddressNotFound && (
        <DialogBox id="address-not-found-modal">
          <DialogBoxHeader id="address-not-found-modal-header">
            <p>{t('confirmAddress.addressNotFound.title')}</p>
          </DialogBoxHeader>
          <DialogBoxBody id="address-not-found-modal-body">
            <p className="pad-1-t pad-2-b row-md-2">
              {t('confirmAddress.addressNotFound.content')}<br />
              {t('confirmAddress.addressNotFound.contentExtend')}
            </p>
            <ButtonPrimary id="address-not-found-button" size="md" onClick={() => {
              setShowAddressNotFound(false);

            }}>
              {t('confirmAddress.addressNotFound.button')}
            </ButtonPrimary>  </DialogBoxBody>
        </DialogBox>
      )}
    </div>
  );
};

export default CompanyInformation;
